<script>
import { hasPremiumAccessHelpers } from "@/utils/helpers";
import axios from "axios";
import SocketioService from "@/services/socketio.service.js";

export default {
  name: "TemplateSidebar",
  mounted() {
    this.hasPremiumAccess = hasPremiumAccessHelpers(this.$store.state.userRoot);

    if (this.showMessagesMenu) {
      this.fetchMessagesUnreadCount();
      const hasSocketRealtime = this.bindMessagesSocketListeners();
      this.messagesUnreadTimer = setInterval(
        this.fetchMessagesUnreadCount,
        hasSocketRealtime ? 90000 : 30000
      );
    }
    window.addEventListener(
      "messages-unread-count-updated",
      this.handleMessagesUnreadCountUpdated
    );
    window.addEventListener("sidebar-theme-changed", this.onSidebarThemeChanged);
    this.$root.$on("novedades-unread", this.setNovedadesUnread);
  },
  beforeDestroy() {
    if (this.messagesUnreadTimer) {
      clearInterval(this.messagesUnreadTimer);
    }
    this.unbindMessagesSocketListeners();
    window.removeEventListener(
      "messages-unread-count-updated",
      this.handleMessagesUnreadCountUpdated
    );
    window.removeEventListener("sidebar-theme-changed", this.onSidebarThemeChanged);
    this.$root.$off("novedades-unread", this.setNovedadesUnread);
  },
  data() {
    return {
      isExpanded: false,
      isHovered: false,
      invoicingMenuOpen: ["InvoiceList", "GuestList", "GuestDetail"].includes(
        this.$route?.name
      ),
      hasPremiumAccess: null,
      messagesUnreadCount: 0,
      novedadesUnread: 0,
      messagesUnreadTimer: null,
      messagesSocketEventName: null,
      messagesChatEventName: null,
      isFetchingMessagesUnreadCount: false,
      // 'light' | 'dark' — intercambiable desde el menú de diseño (persistido).
      sidebarTheme: (() => {
        try { return localStorage.getItem("sidebarTheme") === "light" ? "light" : "dark"; }
        catch (e) { return "dark"; }
      })(),
    };
  },

  computed: {
    // Tipo de panel activo: 'apartment' | 'hotel' — controlado por el switch del nav
    panelType() {
      return this.$store.state.panelType || "apartment";
    },
    isHotelMode() {
      return this.panelType === "hotel";
    },

    // true cuando el usuario logueado es un sub-usuario con rol 'propietario'
    isPropietario() {
      return this.$store.state.subUser?.sub_role === "propietario";
    },

    isActiveConfiguration() {
      const names = [
        "editionProperty",
        "nameProperty",
        "locationProperty",
        "serviceProperty",
        "capacityProperty",
        "priceProperty",
        "roomTypes",
        "roomManagement",
        "Rates",
        "descriptionProperty",
        "Ofertas",
        "Habitaciones",
        "Authorities",
        "Notifications",
        "QRPropiedad",
        "SeguridadPropiedad",
        "identityVerification",
        "Review",
        "syncCalendar",
        "ImageAccommodation",
        "accommodationPolicies",
        "welcomeAccommodation",
        "regionAccommodation",
        "pageManageChatBot",
        "DirectBookingsSettings",
      ];
      return names.includes(this.$route.name);
    },
    property() {
      return this.$store.state.property.property;
    },
    user() {
      return this.$store.state.userRoot;
    },
    showElectronicInvoicingMenu() {
      return (
        this.$store.state.userRoot?.servicios?.electronic_invoicing === true
      );
    },
    // Venta por horas («modo motel»): se enciende cliente a cliente desde el manager.
    // Un hotel normal no vende por horas, así que ser hotel no basta. La cerradura de
    // verdad está en la API (`contrato:hourly_calendar`); esto solo evita enseñar una
    // puerta que no abre.
    showHourlyCalendarMenu() {
      return this.$store.state.userRoot?.servicios?.hourly_calendar === true;
    },
    // La recepción telefónica se contrata aparte. La cerradura de verdad está en
    // la API (`contrato:voice_agent`); esto solo evita enseñar una puerta que no
    // abre a quien no la tiene.
    showVoiceCallsMenu() {
      return this.$store.state.userRoot?.servicios?.voice_agent === true;
    },
    showMessagesMenu() {
      // WhatsApp Cloud propio (empresa con WABA) → mensajería completa.
      const hasCompanyWaba =
        !!this.user?.waba_id && !!this.user?.identificador_number_id;
      // Los clientes QR check-in (sin WABA propia) TAMBIÉN ven "Mensajes" para
      // gestionar los chats de SUS reservas (canal web del Conserje + WhatsApp
      // compartido). El backend ya limita los chats a las propiedades del dueño
      // (botController::retrieveChats → property_id IN propiedades del user_id),
      // así que un QR check-in solo verá los suyos.
      // Para volver a exigir WABA: `return hasCompanyWaba;`
      return hasCompanyWaba || !!this.user?.id;
    },
    showExperiencesMenu() {
      return this.hasPremiumAccess;
    },
  },
  watch: {
    "$route.name"(name) {
      if (["InvoiceList", "GuestList", "GuestDetail"].includes(name)) {
        this.invoicingMenuOpen = true;
      }
    },
    showMessagesMenu(val) {
      if (val) {
        this.fetchMessagesUnreadCount();
        const hasSocketRealtime = this.bindMessagesSocketListeners();
        if (!this.messagesUnreadTimer) {
          this.messagesUnreadTimer = setInterval(
            this.fetchMessagesUnreadCount,
            hasSocketRealtime ? 90000 : 30000
          );
        }
      } else {
        this.messagesUnreadCount = 0;
        this.unbindMessagesSocketListeners();
        if (this.messagesUnreadTimer) {
          clearInterval(this.messagesUnreadTimer);
          this.messagesUnreadTimer = null;
        }
      }
    },
  },
  methods: {
    abrirNovedades() {
      this.$root.$emit("open-novedades");
    },
    setNovedadesUnread(n) {
      this.novedadesUnread = Number(n) || 0;
    },
    // ¿El usuario puede ver/usar la función `key`? (anfitrión → siempre true)
    can(key) {
      return this.$store.getters.hasFeature(key);
    },
    goRoute() {
      if (this.isHotelMode) {
        this.$router.push({ name: "hotelReservationCalendar" });
      } else {
        // El MÚLTIPLE es el de partida también en el móvil. Antes el teléfono
        // iba derecho al mensual sin preguntar, y encima no se podía volver
        // porque el conmutador estaba apagado en pantallas estrechas: se
        // entraba a una vista y ya no se salía de ella.
        let savedView = null;
        try { savedView = localStorage.getItem("reservasCalendar:view"); } catch (e) { /* storage no disponible */ }

        this.$router.push({
          name: savedView === "monthly" ? "monthlyCalendar" : "ReservationCalendar",
        });
      }
    },
    toggleSidebar() {
      this.isExpanded = !this.isExpanded;
      this.$emit("sidebar-toggle", this.isExpanded);
    },
    expandSidebar() {
      if (!this.isExpanded) {
        this.isHovered = true;
      }
    },
    collapseSidebar() {
      if (!this.isExpanded) {
        this.isHovered = false;
      }
    },
    toggleInvoicingMenu() {
      this.invoicingMenuOpen = !this.invoicingMenuOpen;
    },
    async fetchMessagesUnreadCount() {
      if (!this.showMessagesMenu) return;
      if (this.isFetchingMessagesUnreadCount) return;
      this.isFetchingMessagesUnreadCount = true;
      try {
        const { data } = await axios.get("/whatsapp-crm/unread-count");
        this.messagesUnreadCount = Number(data?.data?.count || 0);
      } catch (e) {
        this.messagesUnreadCount = 0;
      } finally {
        this.isFetchingMessagesUnreadCount = false;
      }
    },
    handleMessagesUnreadCountUpdated(event) {
      const count = Number(event?.detail?.count);
      if (Number.isFinite(count)) {
        this.messagesUnreadCount = count;
      }
    },
    onSidebarThemeChanged(event) {
      const t = event?.detail?.theme;
      if (t === "light" || t === "dark") this.sidebarTheme = t;
    },
    bindMessagesSocketListeners() {
      const userId = this.user?.id;
      if (!this.showMessagesMenu || !userId || !SocketioService.socket) return false;

      this.messagesSocketEventName = `new-conversation-user-${userId}`;
      this.messagesChatEventName = `chat-message-${userId}`;

      // IMPORTANTE: estos eventos los COMPARTE App.vue, que tiene el handler central
      // que refresca los mensajes en tiempo real. Un socket.off(evento) SIN referencia
      // borra TODOS los listeners de ese evento (también el de App.vue) y el chat deja
      // de actualizarse en vivo. Por eso guardamos nuestros handlers y quitamos SOLO
      // los nuestros por referencia (off(evento, handler)).
      this.unbindMessagesSocketListeners();

      this.onMessagesConversationSocket = () => this.fetchMessagesUnreadCount();
      this.onMessagesChatSocket = () => this.fetchMessagesUnreadCount();

      SocketioService.socket.on(this.messagesSocketEventName, this.onMessagesConversationSocket);
      SocketioService.socket.on(this.messagesChatEventName, this.onMessagesChatSocket);

      return true;
    },
    unbindMessagesSocketListeners() {
      if (!SocketioService.socket) return;
      // Quita SOLO nuestros propios listeners (por referencia), nunca los de App.vue.
      if (this.messagesSocketEventName && this.onMessagesConversationSocket) {
        SocketioService.socket.off(this.messagesSocketEventName, this.onMessagesConversationSocket);
      }
      if (this.messagesChatEventName && this.onMessagesChatSocket) {
        SocketioService.socket.off(this.messagesChatEventName, this.onMessagesChatSocket);
      }
      this.onMessagesConversationSocket = null;
      this.onMessagesChatSocket = null;
    },
  },
};
</script>

<template>
  <div class="position-relative">
    <div
      :class="['sidebar', { 'sidebar-expanded': isExpanded || isHovered, 'sidebar--dark': sidebarTheme === 'dark' }]"
      @mouseover="expandSidebar"
      @mouseleave="collapseSidebar"
    >
      <div class="sidebar__logo">
        <img
          :src="sidebarTheme === 'dark' ? '/favicon_transparente.jpg' : '/img/1x/favicon.jpg'"
          :alt="$t('sidebar.alt.logo')"
          class="img-fluid logo"
          v-show="!isExpanded && !isHovered"
        />
        <img
          src="/img/1x/logo.png"
          :alt="$t('sidebar.alt.logo')"
          class="img-fluid logo"
          v-show="isExpanded || isHovered"
          style="margin-left: 14px"
        />
      </div>
      <div class="sidebar__menu">
        <ul>
          <strong class="letter-menu">{{ $t("sidebar.header.menu") }}</strong>

          <router-link
            v-if="can('dashboard')"
            tag="li"
            class="menu-item mt-3"
            :to="{ name: 'Dashboard' }"
          >
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Dashboard' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 3 3V18a2.25 2.25 0 0 1-2.25 2.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.home")
              }}</span>
            </a>
          </router-link>

          
          <!-- <router-link tag="li" class="menu-item" :to="{ name: 'Analytics' }">
            <a href="#">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
                />
              </svg>

              <span class="title-li" v-if="isExpanded || isHovered">
                Analytics
              </span>
            </a>
          </router-link> -->

          <li v-if="can('calendar')" class="menu-item" @click.prevent="goRoute()">
            <a href="#">
              <svg
                :class="{
                  isActive:
                    $route.name === 'hotelReservationCalendar' ||
                    $route.name === 'ReservationCalendar' ||
                    $route.name === 'monthlyCalendar' ||
                    $route.name === 'ReservationCalendarOta',
                }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6.75 2.994v2.25m10.5-2.25v2.25m-14.252 13.5V7.491a2.25 2.25 0 0 1 2.25-2.25h13.5a2.25 2.25 0 0 1 2.25 2.25v11.251m-18 0a2.25 2.25 0 0 0 2.25 2.25h13.5a2.25 2.25 0 0 0 2.25-2.25m-18 0v-7.5a2.25 2.25 0 0 1 2.25-2.25h13.5a2.25 2.25 0 0 1 2.25 2.25v7.5m-6.75-6h2.25m-9 2.25h4.5m.002-2.25h.005v.006H12v-.006Zm-.001 4.5h.006v.006h-.006v-.005Zm-2.25.001h.005v.006H9.75v-.006Zm-2.25 0h.005v.005h-.006v-.005Zm6.75-2.247h.005v.005h-.005v-.005Zm0 2.247h.006v.006h-.006v-.006Zm2.25-2.248h.006V15H16.5v-.005Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.calendar")
              }}</span>
            </a>
          </li>

          <!-- Calendario por horas («modo motel»): solo en el panel de hotel -->
          <router-link v-if="isHotelMode && showHourlyCalendarMenu && can('calendar')" tag="li" class="menu-item" :to="{ name: 'hourlyCalendar' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'hourlyCalendar' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.hourlyCalendar")
              }}</span>
            </a>
          </router-link>

          <!-- Lo que los huéspedes le han pedido al agente: contraseñas, salidas tarde,
               servicios. Justo debajo de Calendario: es lo que se mira a diario. Mismo
               permiso que «Recepción IA» del menú de la cuenta: la API da 403 en
               `agent-ia/…` a quien no tiene `ai_agent`. -->
          <router-link v-if="can('ai_agent')" tag="li" class="menu-item" :to="{ name: 'AgentRequests' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'AgentRequests' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.aiReception")
              }}</span>
            </a>
          </router-link>

          <router-link v-if="can('reservations')" tag="li" class="menu-item" :to="{ name: 'Reservas' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Reservas' }"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M11 4H21V6H11V4ZM11 8H17V10H11V8ZM11 14H21V16H11V14ZM11 18H17V20H11V18ZM3 4H9V10H3V4ZM5 6V8H7V6H5ZM3 14H9V20H3V14ZM5 16V18H7V16H5Z"
                ></path>
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.reservations")
              }}</span>
            </a>
          </router-link>

          <router-link
            v-if="isHotelMode && can('channel_manager')"
            tag="li"
            class="menu-item"
            :to="{ name: 'availabilityPrices' }"
          >
            <a href="#">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                version="1.1"
              >
                <g
                  stroke="none"
                  stroke-width="1"
                  fill="none"
                  fill-rule="evenodd"
                >
                  <rect x="0" y="0" width="24" height="24"></rect>
                  <path
                    :class="{ isActive: $route.name === 'availabilityPrices' }"
                    d="M3,4 L20,4 C20.5522847,4 21,4.44771525 21,5 L21,7 C21,7.55228475 20.5522847,8 20,8 L3,8 C2.44771525,8 2,7.55228475 2,7 L2,5 C2,4.44771525 2.44771525,4 3,4 Z M10,10 L20,10 C20.5522847,10 21,10.4477153 21,11 L21,13 C21,13.5522847 20.5522847,14 20,14 L10,14 C9.44771525,14 9,13.5522847 9,13 L9,11 C9,10.4477153 9.44771525,10 10,10 Z M10,16 L20,16 C20.5522847,16 21,16.4477153 21,17 L21,19 C21,19.5522847 20.5522847,20 20,20 L10,20 C9.44771525,20 9,19.5522847 9,19 L9,17 C9,16.4477153 9.44771525,16 10,16 Z"
                    fill="currentColor"
                  ></path>
                  <rect
                    fill="currentColor"
                    opacity="0.3"
                    x="2"
                    y="10"
                    width="5"
                    height="10"
                    rx="1"
                  ></rect>
                </g>
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.availabilityRates")
              }}</span>
            </a>
          </router-link>

          <router-link v-if="can('bookings')" tag="li" class="menu-item" :to="{ name: 'Bookings' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Bookings' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.checkins")
              }}</span>
            </a>
          </router-link>

          <!-- Clientes: todas las personas (check-in + facturación), reutilizables -->
          <router-link v-if="can('guests')" tag="li" class="menu-item" :to="{ name: 'ClientesDirectorio' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'ClientesDirectorio' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.clientsDirectory")
              }}</span>
            </a>
          </router-link>

          <router-link
            v-if="showMessagesMenu && can('messages')"
            tag="li"
            class="menu-item"
            :to="{ name: 'PageMensajes' }"
          >
            <a href="#" class="menu-link-with-badge">
              <svg
                :class="{ isActive: $route.name === 'PageMensajes' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M7.5 8.25h9m-9 3H12m-3.75 8.25L3 21l1.5-5.25A8.25 8.25 0 1 1 19.5 15"
                />
              </svg>

              <div
                v-if="isExpanded || isHovered"
                class="menu-title-badge-wrapper"
              >
                <span class="title-li">{{ $t("sidebar.items.messages") }}</span>
                <span class="new-badge">{{ $t("sidebar.items.badgeNew") }}</span>
                <span
                  v-if="messagesUnreadCount > 0"
                  class="sidebar-counter-badge"
                >
                  {{ messagesUnreadCount > 99 ? "99+" : messagesUnreadCount }}
                </span>
              </div>
            </a>
          </router-link>

          <!-- <router-link
            tag="li"
            class="menu-item"
            :to="{ name: 'messageChannelPage' }"
          >
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'messageChannelPage' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 3.75H6.912a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859M12 3v8.25m0 0-3-3m3 3 3-3"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{ $t('sidebar.items.inbox') }}</span>
            </a>
          </router-link> -->

          <li
            v-if="showElectronicInvoicingMenu && can('invoicing')"
            class="menu-group"
            :class="{ 'menu-group--active': ['InvoiceList', 'GuestList', 'GuestDetail'].includes($route.name) }"
          >
            <button
              type="button"
              class="menu-group__trigger"
              :aria-expanded="String(invoicingMenuOpen)"
              aria-controls="sidebar-invoicing-menu"
              @click="toggleInvoicingMenu"
            >
              <svg
                :class="{ isActive: ['InvoiceList', 'GuestList', 'GuestDetail'].includes($route.name) }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>

              <div
                v-if="isExpanded || isHovered"
                class="menu-group__heading"
              >
                <span class="menu-group__title">{{ $t("sidebar.items.invoicing") }}</span>
                <svg
                  class="menu-group__chevron"
                  :class="{ 'menu-group__chevron--open': invoicingMenuOpen }"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="m6.5 8 3.5 3.5L13.5 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </button>

            <transition name="sidebar-submenu">
              <ul
                v-show="invoicingMenuOpen && (isExpanded || isHovered)"
                id="sidebar-invoicing-menu"
                class="menu-group__submenu"
              >
                <router-link
                  v-if="can('guests')"
                  tag="li"
                  class="menu-group__item"
                  :class="{ 'menu-group__item--active': ['GuestList', 'GuestDetail'].includes($route.name) }"
                  active-class="menu-group__item--active"
                  :to="{ name: 'GuestList' }"
                >
                  <a href="#">
                    <span>{{ $t("sidebar.items.fiscalCustomers") }}</span>
                  </a>
                </router-link>

                <router-link
                  tag="li"
                  class="menu-group__item"
                  active-class="menu-group__item--active"
                  :to="{ name: 'InvoiceList' }"
                >
                  <a href="#">
                    <span>{{ $t("sidebar.items.invoices") }}</span>
                  </a>
                </router-link>
              </ul>
            </transition>
          </li>

          <router-link
            tag="li"
            class="menu-item"
            :to="{ name: 'Servicios' }"
            v-if="showExperiencesMenu && can('services')"
          >
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Servicios' }"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M20.4668 8.69379L20.7134 8.12811C21.1529 7.11947 21.9445 6.31641 22.9323 5.87708L23.6919 5.53922C24.1027 5.35653 24.1027 4.75881 23.6919 4.57612L22.9748 4.25714C21.9616 3.80651 21.1558 2.97373 20.7238 1.93083L20.4706 1.31953C20.2942 0.89349 19.7058 0.89349 19.5293 1.31953L19.2761 1.93083C18.8442 2.97373 18.0384 3.80651 17.0252 4.25714L16.308 4.57612C15.8973 4.75881 15.8973 5.35653 16.308 5.53922L17.0677 5.87708C18.0555 6.31641 18.8471 7.11947 19.2866 8.12811L19.5331 8.69379C19.7136 9.10792 20.2864 9.10792 20.4668 8.69379ZM12 22C17.5228 22 22 17.5228 22 12C22 11.5972 21.9762 11.2 21.9299 10.8097L19.9437 11.0454C19.9809 11.3584 20 11.677 20 12C20 13.4577 19.6101 14.8243 18.929 16.0013L15.4641 10H17.9295L16.7755 8H12.0003L15.1686 2.51246C14.1729 2.1801 13.1074 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12.0003 4L8.53606 10.0003L6.39495 6.29181C7.83839 4.87429 9.81744 4 12.0003 4ZM9.69076 12.0003L10.8456 10H13.1547L14.3095 12.0001L13.1548 14H10.8453L9.69076 12.0003ZM12.0001 16L9.85814 19.7101C7.81886 19.1448 6.10694 17.7921 5.07026 16H12.0001ZM12.0001 20L15.4642 14.0001L17.605 17.7082C16.1616 19.1257 14.183 20 12.0001 20ZM5.07102 7.99869L8.53588 14H4.25204C4.08751 13.3608 4 12.6906 4 12C4 10.5423 4.38987 9.17568 5.07102 7.99869Z"
                ></path>
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.experiences")
              }}</span>
            </a>
          </router-link>

          <router-link
            v-if="can('housekeeping')"
            tag="li"
            class="menu-item"
            :to="{ name: 'Housekeeping' }"
          >
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Housekeeping' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{ $t("sidebar.items.housekeeping") }}</span>
            </a>
          </router-link>

          <!-- Llamadas de la recepción telefónica: quién llamó, cuándo y qué quería. -->
          <!-- 🚨 Las DOS llaves. Tener contratada la recepción telefónica es de la
               CUENTA; poder entrar en ella es del usuario. Esto solo miraba la
               primera, así que a un propietario —que únicamente debe ver su
               calendario y sus analíticas— le salía «Llamadas» en el menú. -->
          <router-link v-if="showVoiceCallsMenu && can('voice_calls')" tag="li" class="menu-item" :to="{ name: 'VoiceCalls' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'VoiceCalls' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.calls")
              }}</span>
            </a>
          </router-link>

          <router-link v-if="can('analytics')" tag="li" class="menu-item" :to="{ name: 'Analytics' }">
            <a href="#">
              <svg
                :class="{ isActive: $route.name === 'Analytics' }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered"
                >{{ $t("sidebar.items.analytics") }}</span
              >
            </a>
          </router-link>

          <!-- <router-link
            tag="li"
            class="menu-item"
            :to="{
              path: `/business/properties/edition/${this.$store.state.propiedadActiva}/name-property`,
            }"
          >
            <a href="#">
              <svg
                :class="{ isActive: isActiveConfiguration }"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.settings")
              }}</span>
            </a>
          </router-link> -->
        </ul>

        <ul class="mt-3 sidebar__bottom">
          <!-- El centro de ayuda. Enlace de verdad, no un `href="#"` con un
               window.open detrás: así se puede abrir en otra pestaña y se ve a
               dónde lleva antes de pulsar.

               Lleva llave como todo lo demás: no la tenía, y por eso aparecía en
               el menú de un propietario. El soporte lo da el titular de la cuenta,
               no el dueño de uno de los apartamentos. -->
          <li v-if="can('support')" class="menu-item">
            <a href="https://support.wacheckin.com/" target="_blank" rel="noopener">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3 18v-6a9 9 0 0 1 18 0v6"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 19a2 2 0 0 1-2 2h-3v-7h3a2 2 0 0 1 2 2v3ZM3 19a2 2 0 0 0 2 2h3v-7H5a2 2 0 0 0-2 2v3Z"
                />
              </svg>
              <span class="title-li" v-if="isExpanded || isHovered">{{
                $t("sidebar.items.support")
              }}</span>
            </a>
          </li>

          <!-- otros ítems opcionales... -->
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu-link-with-badge {
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.menu-title-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.menu-group {
  list-style: none;
  margin: 0;
}

.menu-group__trigger {
  width: 100%;
  min-height: 58px;
  padding: 15px 0;
  border: 0;
  background: transparent;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition:
    background-color 150ms cubic-bezier(0.23, 1, 0.32, 1),
    color 150ms cubic-bezier(0.23, 1, 0.32, 1),
    transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}

.menu-group__trigger:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(103, 61, 230, 0.42);
  outline: none;
}

.menu-group__trigger:active {
  transform: scale(0.985);
}

.menu-group--active > .menu-group__trigger {
  color: #673de6;
  background: rgba(103, 61, 230, 0.065);
}

.menu-group__trigger > svg {
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
}

.menu-group__heading {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.menu-group__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
}

.menu-group__chevron {
  width: 18px !important;
  height: 18px !important;
  margin-left: auto;
  flex: 0 0 18px !important;
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}

.menu-group__chevron--open {
  transform: rotate(180deg);
}

.menu-group__submenu {
  margin: 0 !important;
  padding: 2px 14px 10px 55px !important;
  list-style: none;
}

.menu-group__item {
  list-style: none;
}

.menu-group__item a {
  position: relative;
  min-height: 32px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  color: #686875;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;
  transition:
    color 150ms cubic-bezier(0.23, 1, 0.32, 1),
    opacity 150ms cubic-bezier(0.23, 1, 0.32, 1);
}

.menu-group__item--active a {
  color: #673de6;
  font-weight: 600;
}

.menu-group__item--active a::before {
  content: "";
  position: absolute;
  left: -14px;
  top: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  transform: translateY(-50%);
}

.menu-group__item a:focus-visible {
  outline: 2px solid rgba(103, 61, 230, 0.42);
  outline-offset: 2px;
  border-radius: 4px;
}

.menu-group__item--active a:focus-visible {
  outline-color: rgba(103, 61, 230, 0.58);
}

.menu-group__item a:active {
  opacity: 0.72;
}

.menu-group__item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu-group__item + .menu-group__item {
  margin-top: 1px;
}

.menu-group__item a,
.menu-group__item a:hover,
.menu-group__item--active a,
.menu-group__item--active a:hover {
  background: transparent;
}

.menu-group__item a:focus:not(:focus-visible) {
  outline: none;
}

.sidebar-submenu-enter-active,
.sidebar-submenu-leave-active {
  transform-origin: top left;
  transition:
    opacity 140ms cubic-bezier(0.23, 1, 0.32, 1),
    transform 170ms cubic-bezier(0.23, 1, 0.32, 1);
}

.sidebar-submenu-enter,
.sidebar-submenu-leave-to {
  opacity: 0;
  transform: translateY(-3px) scale(0.985);
}

.sidebar-submenu-enter-to,
.sidebar-submenu-leave {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.new-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #673de6;
  background: #f3efff;
  white-space: nowrap;
  font-family: "DM Sans", sans-serif !important;
}

.letter-menu {
  font-family: "DM Sans", sans-serif !important;
}

.sidebar-counter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  color: #fff;
  background: #2563eb;
  box-shadow: 0 6px 12px rgba(37, 99, 235, 0.22);
  white-space: nowrap;
}
.isActive {
  color: #5e71e4;
}
.sidebar-expanded {
  text-align: left;
  .letter-menu {
    margin-left: 23px;
  }
}

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 80px;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0px 0px 11px 3px #51515114;
  transition: width 0.3s ease-in-out, background 0.25s ease, box-shadow 0.25s ease;
  overflow-y: auto;
  overflow-x: hidden;

  .letter-menu {
    font-weight: 500;
    color: #585858;
    font-size: 12px;
  }

  .title-li {
    font-weight: 500;
  }

  .menu-item {
    padding: 15px 0;
    text-align: center;
    transition: background 0.16s ease;

    &:hover {
      background: #f1f1f1;
    }

    a {
      color: #000;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      transition: color 0.16s ease;
    }

    svg {
      width: 25px;
    }
  }

  ul {
    margin-top: 30px;
  }

  .sidebar__bottom {
    border-top: 1px solid #eee;
  }

  .logo {
    width: 62%;
    margin-top: 9px;
    border-radius: 10px;
  }
}

/* ── Variante OSCURA del sidebar (intercambiable desde el menú de diseño) ── */
.sidebar.sidebar--dark {
  background: #16161d;
  box-shadow: 2px 0 18px rgba(0, 0, 0, 0.2);

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.12); border-radius: 999px; }

  .letter-menu { color: rgba(255, 255, 255, 0.42); }

  .menu-item {
    &:hover { background: rgba(139, 123, 240, 0.14); }
    &:hover a { color: #fff; }
    a { color: rgba(255, 255, 255, 0.72); }
  }

  .menu-link-with-badge { color: rgba(255, 255, 255, 0.72); }
  .menu-group__trigger { color: rgba(255, 255, 255, 0.72); }
  .menu-group__trigger:focus-visible { color: #fff; }
  .menu-group--active > .menu-group__trigger {
    background: rgba(159, 134, 255, 0.075);
    color: #a98fff;
  }
  .menu-group__item a { color: rgba(255, 255, 255, 0.58); }
  .menu-group__item--active a {
    color: #ad97ff;
  }
  .isActive { color: #9f86ff; }
  .sidebar__bottom { border-top-color: rgba(255, 255, 255, 0.08); }
}

/* ESTADO EXPANDIDO */
.sidebar-expanded {
  width: 240px;

  .menu-group__trigger {
    justify-content: flex-start;
    padding: 12px 14px 10px 20px;
  }

  .menu-item {
    text-align: left;
    padding-left: 20px;

    a {
      justify-content: flex-start;
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .menu-group__trigger:hover {
    background: rgba(103, 61, 230, 0.055);
  }

  .menu-group__item:not(.menu-group__item--active) a:hover {
    color: #3d2b73;
  }

  .sidebar.sidebar--dark .menu-group__trigger:hover {
    background: rgba(159, 134, 255, 0.07);
    color: #fff;
  }

  .sidebar.sidebar--dark .menu-group__item:not(.menu-group__item--active) a:hover {
    color: rgba(255, 255, 255, 0.9);
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-group__trigger,
  .menu-group__item a,
  .menu-group__chevron,
  .sidebar-submenu-enter-active,
  .sidebar-submenu-leave-active {
    transition-duration: 0.01ms !important;
  }
}

/* Estrella de Novedades en el menú */
.menu-item--novedades .nov-ico-wrap { position: relative; display: inline-flex; }
.menu-item--novedades svg { color: #673de6; }
.menu-item--novedades .nov-dot {
  position: absolute; top: -2px; right: -2px;
  width: 8px; height: 8px; border-radius: 50%;
  background: #f43f5e; border: 2px solid #fff;
}

/* ===== RTL (arabe): la barra lateral pasa al lado derecho ===== */
[dir="rtl"] {
  .sidebar { left: auto; right: 0; }
  .menu-group__chevron { margin-left: 0; margin-right: auto; }
  .menu-group__item--active a::before { left: auto; right: -14px; }
  .sidebar-expanded .letter-menu { margin-left: 0; margin-right: 23px; }
  .menu-item--novedades .nov-dot { right: auto; left: -2px; }
}
</style>
